/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useState } from "react";

import Intercom from "@intercom/messenger-js-sdk";
import cn from "classnames";
import { useLocation } from "react-router-dom";

import { ROUTES_LIST } from "Router";

import { useAuth, usePrimaryEvents, useUI } from "state";

import RoutesWrapper from "./Router/RoutesWrapper";

import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import { SafeComponent } from "Router/RouterManagers/RouteManager";
import Stack from "components/ui/Stack";
import Drawer from "components/ui/Drawer";
import Overlay from "components/ui/Overlay";
import Header from "components/ui/Header";
import Portal from "components/ui/Portal";
import Sidebar from "components/ui/Sidebar/Sidebar";
import SupportChatLauncher from "SupportChatLauncher";

const Layout = () => {
  const { pathname } = useLocation();

  const {
    isLeftDrawerOpen,
    isRightDrawerOpen,
    leftDrawerData,
    rightDrawerData,
    closeLeftDrawer,
    closeRightDrawer,
    overlayFunction,
    isModalOpen,
    isSidebarOpen,
    modalData,
    closeModal,
    closeNestedModal,
    isNestedModalOpen,
    nestedModalData,
    isSidebarShrunk,
    isActionBarOpen,
    actionBarData,
    resetAll,
  } = useUI();

  const { isLoggedIn } = useAuth();
  const [localStorageChange, setLocalStorageChange] = useState(0);
  const { handleRemoveEvent, selectedEvent } = usePrimaryEvents();
  const { intercom, isDevelopment } = useEnvs((state) => state);
  const isSidebarRendered = isLoggedIn;
  useEffect(() => {
    if (!isLoggedIn) resetAll();
  }, [isLoggedIn]);

  useEffect(() => {
    const handleStorageChange = () => {
      setLocalStorageChange((prev) => prev + 1);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const isCanceledItem = localStorage.getItem("isCanceledItem");
    if (isCanceledItem) {
      handleRemoveEvent();
      localStorage.setItem("eventId", `${selectedEvent?.id}`);
      localStorage.removeItem("isCanceledItem");
    }
  }, [localStorageChange]);

  useEffect(() => {
    if (intercom) {
      Intercom({
        app_id: intercom,
        hide_default_launcher: true,
      });
    } else {
      console.error("Intercom app ID is not defined.");
    }
  }, [intercom]);

  return (
    <Stack test-id="NK303-33">
      {isDevelopment && isLoggedIn && (
        <>
          <ScrollToBottomButton />
        </>
      )}

      {isLoggedIn && <SupportChatLauncher />}

      <Overlay
        className="z-[100]"
        variant="page"
        isOpen={isModalOpen}
        onClick={modalData.isClosableByOverlay && closeModal}
        test-id="nnNCr4pE"
      >
        {isModalOpen && (
          <Portal
            isOpen={isModalOpen}
            children={modalData.content}
            className="[&:has(.parent-size-full)]:h-full [&:has(.parent-size-full)]:w-full [&:has(.parent-size-full)]:p-0"
            test-id="MrlpVQBP"
          />
        )}
      </Overlay>

      <Overlay
        className="z-[200]"
        variant="page"
        isOpen={isNestedModalOpen}
        onClick={closeNestedModal}
        test-id="eD61o2nY"
      >
        {isNestedModalOpen && (
          <Portal
            isOpen={isNestedModalOpen}
            children={nestedModalData.content}
            id="nested-portal"
            test-id="cg43Sv6k"
          />
        )}
      </Overlay>

      <Overlay
        className="z-[50]"
        variant="component"
        isOpen={isLeftDrawerOpen}
        onClick={() => leftDrawerData.isClosableByOverlay && closeLeftDrawer()}
        test-id="30bdgEQz"
      />

      <Drawer
        side="left"
        children={leftDrawerData.content}
        isOpen={isLeftDrawerOpen}
        test-id="pVeIYfhP"
      />

      <Overlay
        className="z-[50]"
        variant="component"
        isOpen={isRightDrawerOpen}
        onClick={() => {
          if (overlayFunction.current) overlayFunction.current();
          closeRightDrawer();
        }}
        test-id="JhnIN7-f"
      />

      <Drawer
        side="right"
        children={rightDrawerData.content}
        isOpen={isRightDrawerOpen}
        test-id="F3HKPiiu"
      />

      <Stack>
        {isLoggedIn && (
          <Header isSidebarRendered={isSidebarRendered} test-id="cDYc5pdf" />
        )}

        <main className="flex h-screen" test-id="GkKFaKVE">
          {isSidebarRendered && (
            <div className="relative" test-id="ZxQRe2vz">
              <Sidebar
                isOpen={isSidebarOpen}
                isLoggedIn={isLoggedIn}
                test-id="NFIme-aa"
              />
            </div>
          )}

          <div className="w-full h-full flex flex-col justify-between relative ">
            <div
              className={cn(
                {
                  [actionBarData.containerTopElementClassName]: isActionBarOpen,
                },
                "mt-s60 w-full h-full duration-200 transition-transform",
                {
                  "ml-auto": isSidebarRendered,
                  "content-width-minus-sidebar":
                    !isSidebarShrunk && isSidebarRendered,
                  "content-width-minus-sidebar-shrunk":
                    isSidebarShrunk && isSidebarRendered,
                },
                {
                  "px-2 sm:px-8 pb-8": !pathname.includes(
                    ROUTES_LIST.DISCOVER.routeMap?.base,
                  ),
                },
              )}
              test-id="2lxxZS7p"
            >
              <RoutesWrapper test-id="ez38v3DL" />
            </div>

            <SafeComponent>
              <div
                className={cn(
                  "w-full flex justify-stretch duration-200 transition-all fixed left-0 right-0 bottom-0 h-[88px]",
                  actionBarData.containerClassName,
                  {
                    "ml-auto": isSidebarRendered,
                    "translate-y-[100%]": !isActionBarOpen,
                    "translate-y-0": isActionBarOpen,
                    "content-width-minus-sidebar":
                      !isSidebarShrunk && isSidebarRendered,
                    "content-width-minus-sidebar-shrunk":
                      isSidebarShrunk && isSidebarRendered,
                  },
                )}
              >
                <div className="w-full h-full flex">
                  <Portal
                    className="w-full h-full flex !p-0"
                    children={actionBarData.content}
                    isOpen={isActionBarOpen}
                    id="action-bar-portal"
                  />
                </div>
              </div>
            </SafeComponent>
          </div>
        </main>
      </Stack>
    </Stack>
  );
};

export default Layout;

const ScrollToBottomButton = () => {
  const [isVisible, setIsVisible] = useState(true);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const { pathname } = useLocation();
  useEffect(() => {
    const hasVScroll = hasVerticalScroll();
    if (hasVScroll) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [pathname]);

  return (
    <button
      className={cn(
        "fixed bottom-8 right-8 z-50 bg-white rounded-full p-2",
        isVisible ? "opacity-100 visible" : "opacity-0 invisible",
      )}
      onClick={scrollToBottom}
      test-id="SwxEggyJ"
    >
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        test-id="SvP7o4l4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 14l-7 7m0 0l-7-7m7 7V3"
          test-id="xYPBQydV"
        />
      </svg>
    </button>
  );
};

function hasVerticalScroll() {
  if (window.innerHeight) {
    return document.body.offsetHeight > window.innerHeight;
  } else {
    return (
      document.documentElement.scrollHeight >
        document.documentElement.offsetHeight ||
      document.body.scrollHeight > document.body.offsetHeight
    );
  }
}
